// src/views/AnalysisView/AnalysisStatus.jsx

import React from 'react'
import { Box, Tooltip } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import AddCardIcon from '@mui/icons-material/AddCard'
import TonalChip from '../FileUploadView/components/TonalChip'
import { refusalSentence } from 'src/utils/importRefusal'

// The analysis list endpoint returns `analysisRow.steps` already shaped as the
// pipeline-map lanes to render — the backend (libra `pipeline_steps.build_pipeline_lanes`)
// is the single source of truth for names, order and which branches are shown
// (non-requested CNV/SV/methylation are omitted there). Each lane is one flat phase:
//   { title, state: 'pending'|'running'|'done'|'failed' }   // no sub-steps
// A phase the pipeline ran past without executing (backend `skipped`) is treated here
// as done — see buildLanes — so it reads as a finished phase, not a special state.
// This component only renders them; it holds no pipeline structure of its own.
// Tooltips stay dark in both modes, so the phase list uses the light shades.
const STEP_STATE_TONE = {
  done: 'success',
  running: 'info',
  failed: 'error',
}

// Per-phase marker instead of a sequential number: the pipeline is a DAG with
// parallel branches, so numbering (1. 2. 3.) would falsely imply an order.
const STEP_STATE_GLYPH = {
  done: '✓',
  running: '●',
  failed: '✕',
  pending: '○',
}

// Defensive normalise of the backend lanes to the render shape. The backend already
// sends this shape; this guards stale/legacy cached responses. A `skipped` phase (one
// the pipeline ran past without executing) is folded into `done` so it counts and
// renders as finished.
// The AI phase lane, as titled by the backend (libra `pipeline_steps.PIPELINE_VIEWS`).
export const AI_LANE_TITLE = 'AI Clinical Assessment'

// Whether this analysis asked for AI. Both the original request and a later Run AI set
// `analysis_parameters.annotation.runAI` and the 'AI' tag, so either one is enough.
// Returns null when the row carries neither field (a caller that passes a partial row),
// so the AI phase is only hidden when the row positively says AI was not requested.
export const aiRequested = (analysisRow) => {
  const annotation = analysisRow?.analysis_parameters?.annotation
  const tags = Array.isArray(analysisRow?.tags) ? analysisRow.tags : null
  if (annotation?.runAI || tags?.includes('AI')) return true
  if (annotation || tags) return false
  return null
}

// Whether the row's lanes are exact and so must be shown as sent. A tumour row from a
// current server carries `steps_settled` (a boolean): its copy number and structural
// stage runs on after the analysis itself is DONE, so a DONE row can still hold a
// running or failed lane. Rows without the key (germline rows, an older server) keep
// the old behaviour: a DONE row reads every phase as done.
export const lanesAreExact = (analysisRow) => typeof analysisRow?.steps_settled === 'boolean'

// The sentence a FAILED somatic copy number or structural upload shows: the neutral
// import sentence plus the server's own explanation (never its code). Null on any
// other row, so a row without `refusal` (an older server) reads as before.
// A tumour FASTQ run whose tumour and normal were not confirmed as one person
// (libra #1957) is not a file import: its sentence is shown on its own, without
// the import sentence in front. A missing sentence there shows nothing extra.
export const RUN_IDENTITY_REFUSAL_CODES = Object.freeze([
  'identity_mismatch',
  'identity_report_missing',
  'identity_pair_not_in_report',
])

export const refusalTextOf = (status, analysisRow) => {
  const refusal = analysisRow?.refusal
  if (status !== 'FAILED' || !refusal || typeof refusal !== 'object') return null
  if (analysisRow.analysis_type === 'FASTQ' || RUN_IDENTITY_REFUSAL_CODES.includes(refusal.reason)) {
    const text = typeof refusal.reason_text === 'string' ? refusal.reason_text.trim() : ''
    return text || null
  }
  return refusalSentence(refusal.reason_text)
}

export const buildLanes = (analysisRow) => {
  // The backend keeps the AI lane whenever an AI task left a record, but that task
  // starts and exits within milliseconds when AI was not requested. It is not a phase
  // this analysis runs, so it is neither listed nor counted.
  const hideAI = aiRequested(analysisRow) === false
  return (Array.isArray(analysisRow?.steps) ? analysisRow.steps : [])
    .filter((lane) => !(hideAI && lane?.title === AI_LANE_TITLE))
    .map((lane) => ({
      title: lane?.title ?? '',
      state: lane?.state === 'skipped' ? 'done' : lane?.state,
    }))
}

const StatusChip = ({ status, lanes = [] }) => {
  let tone = 'info'
  let label = status || 'UNKNOWN'

  switch (status) {
    case 'DONE':
      tone = 'success'
      label = 'Done'
      break
    case 'FAILED':
      tone = 'error'
      label = 'Failed'
      break
    case 'UNKNOWN':
      tone = 'error'
      label = 'Unknown'
      break
    default: {
      // Any in-progress state: 'RUNNING', 'PENDING', or a stale granular progress label
      // an older backend leaked into `status` (e.g. "Literature Linking"). Never render
      // that raw string — derive the chip text from the same steps[] lanes the swimlane
      // shows, so the chip and the boxes always agree regardless of what `status` carries.
      tone = 'info'
      const running = lanes.filter((lane) => lane.state === 'running')
      if (running.length === 1) {
        label = running[0].title
      } else if (running.length > 1) {
        label = `${running.length} tasks are running`
      } else {
        // Zero tasks running yet — all pending (queued) or a brief gap between phases.
        // Show a plain "Running" rather than "0 tasks are running".
        label = 'Running'
      }
      break
    }
  }

  // Long states ("VCF Preprocessing", "3 tasks are running") wrap onto a second line
  // instead of widening the Status column and pushing the table sideways.
  return (
    <TonalChip
      tone={tone}
      label={label}
      dot
      style={{
        maxWidth: 124,
        height: 'auto',
        minHeight: 22,
        padding: '3px 8px',
        whiteSpace: 'normal',
        lineHeight: 1.25,
      }}
    />
  )
}

// Always-visible, at-a-glance progress as a plain "X/Y" done count: completed phases
// out of the total. Order-agnostic, so parallel branches simply add to the numerator
// as they finish — no implied linear order (LID-230). The per-phase breakdown still
// lives in the hover swimlane.
const ProgressCount = ({ doneCount, total }) => (
  <Box
    sx={{
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: 'text.secondary',
      lineHeight: 1,
      fontVariantNumeric: 'tabular-nums',
    }}
    aria-label={`${doneCount}/${total} tasks done`}
    title={`${doneCount}/${total} tasks done`}
  >
    {doneCount}/{total}
  </Box>
)

// One phase line of the breakdown: a bold label with its state glyph. Every lane is a
// single flat phase now (no sub-steps), so all rows render identically.
const StepRow = ({ label, state }) => {
  const theme = useTheme()
  const tone = STEP_STATE_TONE[state]
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '6px',
        fontSize: '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.5,
        color: tone ? theme.palette[tone].light : 'inherit',
      }}
    >
      <span style={{ width: 12, textAlign: 'center', flexShrink: 0 }}>{STEP_STATE_GLYPH[state] || '○'}</span>
      <span>{label}</span>
    </Box>
  )
}

// Hover breakdown as a plain vertical text list: one glyph+title line per phase. Phases
// keep backend order top-to-bottom; parallel branches are just adjacent phase lines —
// no arrows, no implied sequence (LID-230).
const PhaseList = ({ lanes, clampDone }) => (
  <Box sx={{ py: 0.25 }}>
    {lanes.map((lane, li) => (
      // Clamp lagging phases to done on a completed run.
      <StepRow key={li} label={lane.title} state={clampDone ? 'done' : lane.state} />
    ))}
  </Box>
)

// A somatic row's annotation state (somatic roadmap R1.18, owner decision D-7):
// the backend sends `annotation_degraded` on somatic rows only, as
// {degraded, sources} or null when no provenance was recorded yet.
const SOURCE_LABELS = { cosmic: 'COSMIC', oncokb: 'OncoKB' }

export const DegradedMark = ({ annotationDegraded }) => {
  if (!annotationDegraded?.degraded) return null
  const sources = (annotationDegraded.sources || []).map((s) => SOURCE_LABELS[s] || s)
  const names = sources.length ? sources.join(', ') : 'annotation'
  return (
    <Tooltip
      arrow
      title={`Somatic annotation degraded (${names}). An empty value from ${
        sources.length > 1 ? 'these sources' : 'this source'
      } is not a negative; open the analysis for the reason.`}
    >
      <Box
        data-testid="annotation-degraded"
        sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'warning.main', lineHeight: 1 }}
      >
        Degraded: {names}
      </Box>
    </Tooltip>
  )
}

const AnalysisStatus = ({ status, analysisRow }) => {
  const isDone = status === 'DONE'
  const isFailed = status === 'FAILED'
  const isUnknown = status === 'UNKNOWN'
  const showRefund = isFailed

  // Pipeline-map lanes with live state — built by the backend, rendered as-is.
  const lanes = buildLanes(analysisRow)
  const hasLanes = lanes.length > 0

  // Order-agnostic progress at the phase level: completed phases out of the total.
  // When the pipeline as a whole is DONE, clamp so the count never lags the state,
  // unless the row's lanes are exact (a tumour row): those are rendered as sent.
  const clampDone = isDone && !lanesAreExact(analysisRow)
  const total = lanes.length
  const doneCount = clampDone ? total : lanes.filter((lane) => lane.state === 'done').length
  const refusalText = refusalTextOf(status, analysisRow)

  // The done count only makes sense while progressing / when complete — a failed
  // run stalled partway, so an X/Y there would read as "in progress".
  const showCount = hasLanes && !isFailed && !isUnknown
  // The hover breakdown shows whenever we have phase data, INCLUDING failed runs: the
  // failing phase is marked `failed` (✕) by the backend, so the swimlane pinpoints
  // where the pipeline broke. Only UNKNOWN (no pipeline) has nothing to show.
  const showTooltip = (hasLanes && !isUnknown) || Boolean(refusalText)
  const tooltipTitle = (
    <>
      {refusalText && <Box sx={{ fontSize: '0.8125rem', lineHeight: 1.5, mb: hasLanes ? 0.5 : 0 }}>{refusalText}</Box>}
      {hasLanes && <PhaseList lanes={lanes} clampDone={clampDone} />}
    </>
  )

  return (
    <Box sx={{ width: 150, textAlign: 'center' }}>
      <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 4 }}>
        <Box display="inline-flex" alignItems="center" style={{ gap: 5 }}>
          {showTooltip ? (
            <Tooltip title={tooltipTitle} arrow>
              <span>
                <StatusChip status={status} lanes={lanes} />
              </span>
            </Tooltip>
          ) : (
            <StatusChip status={status} lanes={lanes} />
          )}
          {showRefund && analysisRow?.is_refunded && analysisRow?.analysis_price != null && (
            <Tooltip title={`Refunded ${analysisRow.analysis_price} Credits`} arrow>
              <Box component="span" sx={{ fontSize: 14, color: 'success.main', display: 'inline-flex' }}>
                <AddCardIcon fontSize="inherit" />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Plain X/Y done count (parallel-aware; order-agnostic) */}
        {showCount && <ProgressCount doneCount={doneCount} total={total} />}
        {refusalText && (
          <Box
            data-testid="analysis-refusal"
            sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3, maxWidth: 150 }}
          >
            {refusalText}
          </Box>
        )}
        <DegradedMark annotationDegraded={analysisRow?.annotation_degraded} />
      </Box>
    </Box>
  )
}

export default AnalysisStatus
